import moment from 'moment-timezone';
import firebase from '../config/firebase.config';
import httpClient from '../config/httpClient';

const db = firebase.firestore();
const { Timestamp } = firebase.firestore;

const toTimestamp = (date) => Timestamp.fromDate(date);

const toDateOrNull = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

/** Sessions that overlap [rangeStart, rangeEnd] (inclusive day bounds in member TZ). */
const sessionOverlapsRequestedRange = (
  session,
  rangeStartMoment,
  rangeEndMoment
) => {
  const sStart = toDateOrNull(session?.startTime);
  if (!sStart) return false;
  const sEnd =
    toDateOrNull(session?.endTime) ||
    toDateOrNull(session?.lastPingAt) ||
    sStart;

  const wStart = rangeStartMoment.valueOf();
  const wEnd = rangeEndMoment.valueOf();
  return sStart.getTime() <= wEnd && sEnd.getTime() >= wStart;
};

const isZeroDurationSession = (session) => {
  const start = toDateOrNull(session?.startTime);
  const end = toDateOrNull(session?.endTime || session?.lastPingAt);
  if (!start || !end) return false;

  const durationSec = Number(session?.duration) || 0;
  const diffSec = Math.floor((end.getTime() - start.getTime()) / 1000);

  return diffSec <= 0 && durationSec <= 0;
};

const toHoursMinutes = (seconds = 0) => {
  const minutes = Math.floor(seconds / 60);
  return {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  };
};

/**
 * Fetch sessions for a specific day offset (0 = today, 1 = yesterday)
 * and aggregate total, idle and productive time.
 */
async function getSessionsForDay({
  companyId,
  employeeId,
  dayOffset = 0,
  timeZone,
}) {
  if (!companyId || !employeeId) {
    return { success: false, error: 'companyId and employeeId are required' };
  }

  const tz = timeZone || moment.tz.guess();
  const dayStart = moment().tz(tz).startOf('day').subtract(dayOffset, 'days');
  const dayEnd = dayStart.clone().endOf('day');

  const startTs = toTimestamp(dayStart.toDate());
  const endTs = toTimestamp(dayEnd.toDate());

  const sessionsRef = db
    .collection('desktopApp')
    .doc(companyId)
    .collection('employees')
    .doc(employeeId)
    .collection('sessions');

  const snapshot = await sessionsRef
    .where('startTime', '>=', startTs)
    .where('startTime', '<=', endTs)
    .get();

  const sessions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  let totalSeconds = 0;
  let idleSeconds = 0;
  let firstStart = null;
  let lastEnd = null;

  sessions.forEach((session) => {
    const duration = session.duration || 0;
    const idle = session.idleDuration || 0;
    totalSeconds += duration;
    idleSeconds += idle;

    const start = session.startTime ? session.startTime.toDate() : null;
    const end =
      session.endTime?.toDate() || session.lastPingAt?.toDate() || null;

    if (start && (!firstStart || start < firstStart)) {
      firstStart = start;
    }
    if (end && (!lastEnd || end > lastEnd)) {
      lastEnd = end;
    }
  });

  const productiveSeconds = Math.max(0, totalSeconds - idleSeconds);

  const total = toHoursMinutes(totalSeconds);
  const idle = toHoursMinutes(idleSeconds);
  const productive = toHoursMinutes(productiveSeconds);

  return {
    success: true,
    data: {
      sessions,
      totals: {
        totalHours: total.hours,
        totalMinutes: total.minutes,
        idleHours: idle.hours,
        idleMinutes: idle.minutes,
        productiveHours: productive.hours,
        productiveMinutes: productive.minutes,
        startTime: firstStart,
        endTime: lastEnd,
        timeZone: tz,
      },
    },
  };
}

export default {
  getSessionsForDay,
  /**
   * Fetch sessions for a date range (inclusive) and return raw sessions.
   * Supports dual-path system:
   * - Old path (desktopApp/{companyId}/employees/{employeeId}/sessions) for dates before Jan 12, 2025 IST
   * - New path (teamDatabase/{teamId}/members/{userUID}/sessions) for dates on/after Jan 12, 2025 IST
   */
  async getSessionsForRange({
    teamId,
    userUID,
    companyId,
    employeeId,
    startDate,
    endDate,
    timeZone,
  }) {
    console.log('🔵 [getSessionsForRange] ===== FUNCTION CALLED =====', {
      teamId,
      userUID,
      companyId,
      employeeId,
      startDate,
      endDate,
      timeZone,
      timestamp: new Date().toISOString(),
    });

    // Validate required parameters
    if (!startDate || !endDate) {
      console.error('❌ [getSessionsForRange] Missing required parameters', {
        startDate,
        endDate,
      });
      return { success: false, error: 'startDate and endDate are required' };
    }

    // Determine which path(s) to use based on date range
    const tz = timeZone || moment.tz.guess();
    const start = moment.tz(startDate, 'YYYY-MM-DD', tz).startOf('day');
    const end = moment.tz(endDate, 'YYYY-MM-DD', tz).endOf('day');

    console.log('📅 [getSessionsForRange] Date calculations:', {
      inputStartDate: startDate,
      inputEndDate: endDate,
      inputTimeZone: tz,
      parsedStart: start.format('YYYY-MM-DD HH:mm:ss Z'),
      parsedEnd: end.format('YYYY-MM-DD HH:mm:ss Z'),
      startUTC: start.utc().format('YYYY-MM-DD HH:mm:ss'),
      endUTC: end.utc().format('YYYY-MM-DD HH:mm:ss'),
    });

    // Convert to IST for cutoff comparison (IST = UTC+5:30)
    const startIST = start.clone().tz('Asia/Kolkata');
    const endIST = end.clone().tz('Asia/Kolkata');
    const cutoffDate = moment
      .tz('2026-01-12', 'YYYY-MM-DD', 'Asia/Kolkata')
      .startOf('day');

    console.log('🌏 [getSessionsForRange] IST conversion:', {
      startIST: startIST.format('YYYY-MM-DD HH:mm:ss Z'),
      endIST: endIST.format('YYYY-MM-DD HH:mm:ss Z'),
      cutoffDate: cutoffDate.format('YYYY-MM-DD HH:mm:ss Z'),
      cutoffDateUTC: cutoffDate.utc().format('YYYY-MM-DD HH:mm:ss'),
    });

    // Determine which path(s) to query
    const useOldPath = endIST.isBefore(cutoffDate);
    const useNewPath = startIST.isSameOrAfter(cutoffDate);
    const useBothPaths = !useOldPath && !useNewPath; // Range spans the cutoff

    console.log('🔀 [getSessionsForRange] Path selection logic:', {
      endIST_isBefore_cutoff: endIST.isBefore(cutoffDate),
      startIST_isSameOrAfter_cutoff: startIST.isSameOrAfter(cutoffDate),
      useOldPath,
      useNewPath,
      useBothPaths,
    });

    console.log('📊 [getSessionsForRange] Path selection summary:', {
      startDate,
      endDate,
      timeZone: tz,
      startIST: startIST.format('YYYY-MM-DD HH:mm:ss Z'),
      endIST: endIST.format('YYYY-MM-DD HH:mm:ss Z'),
      cutoffDate: cutoffDate.format('YYYY-MM-DD HH:mm:ss Z'),
      useOldPath,
      useNewPath,
      useBothPaths,
      hasCompanyId: !!companyId,
      hasEmployeeId: !!employeeId,
      hasTeamId: !!teamId,
      hasUserUID: !!userUID,
      companyId,
      employeeId,
      teamId,
      userUID,
    });

    // Widen query window by one day so sessions that started the prior calendar day
    // but overlap the requested range are included; then filter by overlap below.
    const queryFrom = start.clone().subtract(1, 'day').startOf('day');
    const queryLowerTs = toTimestamp(queryFrom.toDate());
    const endTs = toTimestamp(end.toDate());

    console.log('⏰ [getSessionsForRange] Timestamp conversions:', {
      requestedRangeStart: start.format('YYYY-MM-DD HH:mm:ss Z'),
      queryLowerBound: queryFrom.format('YYYY-MM-DD HH:mm:ss Z'),
      queryLowerTs: queryLowerTs.toDate().toISOString(),
      endTs: endTs.toDate().toISOString(),
      queryLowerTsSeconds: queryLowerTs.seconds,
      endTsSeconds: endTs.seconds,
    });

    try {
      const allSessions = [];

      // Query old path if needed
      if (useOldPath || useBothPaths) {
        console.log('🔍 [getSessionsForRange] ===== QUERYING OLD PATH =====', {
          useOldPath,
          useBothPaths,
          companyId: companyId || 'MISSING',
          employeeId: employeeId || 'MISSING',
          userUID: userUID || 'MISSING',
          note: 'employeeId should match the document ID in desktopApp/{companyId}/employees/{employeeId}',
        });
        if (!companyId || !employeeId) {
          console.warn(
            '⚠️ [getSessionsForRange] Old path requested but companyId/employeeId missing',
            {
              companyId: companyId || 'undefined',
              employeeId: employeeId || 'undefined',
            }
          );
        } else {
          try {
            const oldPath = `desktopApp/${companyId}/employees/${employeeId}/sessions`;
            console.log(
              '📂 [getSessionsForRange] Old path collection:',
              oldPath
            );

            const oldSessionsRef = db
              .collection('desktopApp')
              .doc(companyId)
              .collection('employees')
              .doc(employeeId)
              .collection('sessions');

            // If using both paths, only query old path up to cutoff
            const oldEndTs = useBothPaths
              ? toTimestamp(cutoffDate.clone().subtract(1, 'second').toDate())
              : endTs;

            console.log('🔎 [getSessionsForRange] Old path query filters:', {
              startTime_gte: queryLowerTs.toDate().toISOString(),
              startTime_lte: oldEndTs.toDate().toISOString(),
              useBothPaths,
              oldEndTs_seconds: oldEndTs.seconds,
            });

            const queryStartTime = Date.now();
            const oldSnapshot = await oldSessionsRef
              .where('startTime', '>=', queryLowerTs)
              .where('startTime', '<=', oldEndTs)
              .orderBy('startTime', 'desc')
              .get();
            const queryEndTime = Date.now();

            console.log('⏱️ [getSessionsForRange] Old path query completed:', {
              queryDuration: `${queryEndTime - queryStartTime}ms`,
              docsCount: oldSnapshot.docs.length,
              empty: oldSnapshot.empty,
            });

            const oldSessions = oldSnapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                startTime: data.startTime?.toDate
                  ? data.startTime.toDate().toISOString()
                  : data.startTime,
                endTime: data.endTime?.toDate
                  ? data.endTime.toDate().toISOString()
                  : data.endTime,
                duration: data.duration,
                ...data,
              };
            });

            if (oldSessions.length > 0) {
              console.log(
                '📋 [getSessionsForRange] Old path sessions sample (first 3):',
                {
                  total: oldSessions.length,
                  first: oldSessions[0],
                  second: oldSessions[1] || null,
                  third: oldSessions[2] || null,
                  sessionIds: oldSessions.map((s) => s.id),
                }
              );
            }

            allSessions.push(...oldSessions);
            console.log(
              `✅ [getSessionsForRange] Old path: Added ${oldSessions.length} sessions to results`
            );
          } catch (error) {
            console.error('❌ [getSessionsForRange] Error querying old path:', {
              error: error.message,
              stack: error.stack,
              code: error.code,
            });
            // Continue with new path even if old path fails
          }
        }
      } else {
        console.log('⏭️ [getSessionsForRange] Skipping old path (not needed)');
      }

      // Query new path if needed
      if (useNewPath || useBothPaths) {
        console.log('🔍 [getSessionsForRange] ===== QUERYING NEW PATH =====', {
          useNewPath,
          useBothPaths,
          teamId: teamId || 'MISSING',
          userUID: userUID || 'MISSING',
        });
        if (!teamId || !userUID) {
          console.warn(
            '⚠️ [getSessionsForRange] New path requested but teamId/userUID missing',
            {
              teamId: teamId || 'undefined',
              userUID: userUID || 'undefined',
            }
          );
        } else {
          try {
            const newPath = `teamDatabase/${teamId}/members/${userUID}/sessions`;
            console.log(
              '📂 [getSessionsForRange] New path collection:',
              newPath
            );

            const newSessionsRef = db
              .collection('teamDatabase')
              .doc(teamId)
              .collection('members')
              .doc(userUID)
              .collection('sessions');

            // If using both paths, only query new path from cutoff onwards (and not before queryFrom)
            const newStartTs = useBothPaths
              ? toTimestamp(moment.max(cutoffDate, queryFrom).toDate())
              : queryLowerTs;

            console.log('🔎 [getSessionsForRange] New path query filters:', {
              startTime_gte: newStartTs.toDate().toISOString(),
              startTime_lte: endTs.toDate().toISOString(),
              useBothPaths,
              newStartTs_seconds: newStartTs.seconds,
            });

            const queryStartTime = Date.now();
            const newSnapshot = await newSessionsRef
              .where('startTime', '>=', newStartTs)
              .where('startTime', '<=', endTs)
              .orderBy('startTime', 'desc')
              .get();
            const queryEndTime = Date.now();

            console.log('⏱️ [getSessionsForRange] New path query completed:', {
              queryDuration: `${queryEndTime - queryStartTime}ms`,
              docsCount: newSnapshot.docs.length,
              empty: newSnapshot.empty,
            });

            const newSessions = newSnapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                startTime: data.startTime?.toDate
                  ? data.startTime.toDate().toISOString()
                  : data.startTime,
                endTime: data.endTime?.toDate
                  ? data.endTime.toDate().toISOString()
                  : data.endTime,
                duration: data.duration,
                ...data,
              };
            });

            if (newSessions.length > 0) {
              console.log(
                '📋 [getSessionsForRange] New path sessions sample (first 3):',
                {
                  total: newSessions.length,
                  first: newSessions[0],
                  second: newSessions[1] || null,
                  third: newSessions[2] || null,
                  sessionIds: newSessions.map((s) => s.id),
                }
              );
            }

            allSessions.push(...newSessions);
            console.log(
              `✅ [getSessionsForRange] New path: Added ${newSessions.length} sessions to results`
            );
          } catch (error) {
            console.error('❌ [getSessionsForRange] Error querying new path:', {
              error: error.message,
              stack: error.stack,
              code: error.code,
            });
            // Return old path results even if new path fails
          }
        }
      } else {
        console.log('⏭️ [getSessionsForRange] Skipping new path (not needed)');
      }

      console.log('🔄 [getSessionsForRange] ===== PROCESSING RESULTS =====', {
        totalSessionsBeforeDedup: allSessions.length,
        fromOldPath: allSessions.filter((s) => {
          // Try to identify which path this came from (approximate)
          // This is just for logging, actual dedup is by ID
          return true; // We'll log this differently
        }).length,
      });

      // Sort all sessions by startTime descending and remove duplicates (by id)
      const uniqueSessions = [];
      const seenIds = new Set();
      const duplicates = [];

      allSessions
        .sort((a, b) => {
          const aTime = a.startTime?.toDate
            ? a.startTime.toDate().getTime()
            : typeof a.startTime === 'string'
              ? new Date(a.startTime).getTime()
              : 0;
          const bTime = b.startTime?.toDate
            ? b.startTime.toDate().getTime()
            : typeof b.startTime === 'string'
              ? new Date(b.startTime).getTime()
              : 0;
          return bTime - aTime; // Descending order
        })
        .forEach((session) => {
          if (!seenIds.has(session.id)) {
            seenIds.add(session.id);
            uniqueSessions.push(session);
          } else {
            duplicates.push(session.id);
          }
        });

      console.log('📊 [getSessionsForRange] Deduplication results:', {
        totalBeforeDedup: allSessions.length,
        totalAfterDedup: uniqueSessions.length,
        duplicatesFound: duplicates.length,
        duplicateIds: duplicates.slice(0, 5), // Show first 5 duplicates
      });

      const filteredSessions = uniqueSessions.filter(
        (session) => !isZeroDurationSession(session)
      );

      console.log('🧹 [getSessionsForRange] Zero-duration filtering results:', {
        totalBeforeFilter: uniqueSessions.length,
        totalAfterFilter: filteredSessions.length,
        filteredOutCount: uniqueSessions.length - filteredSessions.length,
      });

      const overlapFiltered = filteredSessions.filter((session) =>
        sessionOverlapsRequestedRange(session, start, end)
      );

      console.log(
        '📆 [getSessionsForRange] Overlap filter (requested range):',
        {
          totalBeforeOverlap: filteredSessions.length,
          totalAfterOverlap: overlapFiltered.length,
          requestedStart: start.format('YYYY-MM-DD'),
          requestedEnd: end.format('YYYY-MM-DD'),
        }
      );

      const actuallyQueriedOldPath =
        !!(useOldPath || useBothPaths) && !!(companyId && employeeId);
      const actuallyQueriedNewPath =
        !!(useNewPath || useBothPaths) && !!(teamId && userUID);

      console.log('✅ [getSessionsForRange] ===== FINAL SUMMARY =====', {
        totalUniqueSessions: overlapFiltered.length,
        queriedOldPath: actuallyQueriedOldPath,
        queriedNewPath: actuallyQueriedNewPath,
        useOldPath,
        useNewPath,
        useBothPaths,
        dateRange: `${startDate} to ${endDate}`,
        dateRangeIST: `${startIST.format('YYYY-MM-DD')} to ${endIST.format(
          'YYYY-MM-DD'
        )}`,
        cutoffDate: cutoffDate.format('YYYY-MM-DD'),
        firstSessionStartTime: overlapFiltered[0]?.startTime || 'N/A',
        lastSessionStartTime:
          overlapFiltered[overlapFiltered.length - 1]?.startTime || 'N/A',
        allSessionIds: overlapFiltered.map((s) => s.id),
      });

      // Additional diagnostic: Check if we should have queried both paths
      if (startIST.isBefore(cutoffDate) && endIST.isAfter(cutoffDate)) {
        console.log(
          'ℹ️ [getSessionsForRange] DIAGNOSTIC: Date range spans cutoff',
          {
            startIST: startIST.format('YYYY-MM-DD HH:mm:ss Z'),
            endIST: endIST.format('YYYY-MM-DD HH:mm:ss Z'),
            cutoffDate: cutoffDate.format('YYYY-MM-DD HH:mm:ss Z'),
            startIST_before_cutoff: startIST.isBefore(cutoffDate),
            endIST_after_cutoff: endIST.isAfter(cutoffDate),
            useBothPaths,
            actuallyQueriedBoth:
              actuallyQueriedOldPath && actuallyQueriedNewPath,
          }
        );
      }

      return { success: true, data: { sessions: overlapFiltered } };
    } catch (error) {
      console.error('Error fetching sessions range', error);
      return { success: false, error };
    }
  },

  /**
   * Fetch activities for a given session, including screenshots and timestamps.
   * Supports dual-path system based on session startTime or explicit path selection.
   */
  async getSessionActivities({
    teamId,
    userUID,
    companyId,
    employeeId,
    sessionId,
    sessionStartTime, // Optional: to determine which path to use
  }) {
    if (!sessionId) {
      return {
        success: false,
        error: 'sessionId is required',
      };
    }

    // Determine which path to use
    let useOldPath = false;
    let useNewPath = false;

    if (sessionStartTime) {
      // Determine path based on session startTime
      // Handle Firestore Timestamp, Date object, or number (milliseconds)
      let dateValue;
      if (
        sessionStartTime.toDate &&
        typeof sessionStartTime.toDate === 'function'
      ) {
        // Firestore Timestamp
        dateValue = sessionStartTime.toDate();
      } else if (sessionStartTime instanceof Date) {
        // Date object
        dateValue = sessionStartTime;
      } else if (typeof sessionStartTime === 'number') {
        // Timestamp in milliseconds
        dateValue = new Date(sessionStartTime);
      } else {
        // Try to parse as-is
        dateValue = sessionStartTime;
      }

      const sessionStart = moment(dateValue);
      const sessionStartIST = sessionStart.tz('Asia/Kolkata');
      const cutoffDate = moment
        .tz('2026-01-12', 'YYYY-MM-DD', 'Asia/Kolkata')
        .startOf('day');
      useOldPath = sessionStartIST.isBefore(cutoffDate);
      useNewPath = !useOldPath;
    } else {
      // If no sessionStartTime provided, try both paths (new first, then old)
      useNewPath = true;
      useOldPath = true;
    }

    try {
      // Try new path first (if applicable)
      if (useNewPath && teamId && userUID) {
        try {
          const newActivitiesRef = db
            .collection('teamDatabase')
            .doc(teamId)
            .collection('members')
            .doc(userUID)
            .collection('sessions')
            .doc(sessionId)
            .collection('activities')
            .orderBy('timestamp', 'asc');

          const newSnapshot = await newActivitiesRef.get();

          if (!newSnapshot.empty) {
            const activities = newSnapshot.docs.map((doc) => {
              const data = doc.data() || {};
              return {
                id: doc.id,
                timestamp: data.timestamp?.toDate
                  ? data.timestamp.toDate()
                  : null,
                screenshots: data.screenshots || [],
                screenshotThumbs: Array.isArray(data.screenshotThumbs)
                  ? data.screenshotThumbs
                  : [],
                appUsage: data.appUsage || {},
                idleTime: data.idleTime || 0,
                totalTimeTracked: data.totalTimeTracked || '',
              };
            });
            console.log(
              `[getSessionActivities] Found ${activities.length} activities from new path`
            );
            return { success: true, data: { activities } };
          }
        } catch (error) {
          console.warn(
            '[getSessionActivities] New path query failed, trying old path:',
            error
          );
          // Fall through to try old path
        }
      }

      // Try old path (if applicable)
      if (useOldPath && companyId && employeeId) {
        try {
          const oldActivitiesRef = db
            .collection('desktopApp')
            .doc(companyId)
            .collection('employees')
            .doc(employeeId)
            .collection('sessions')
            .doc(sessionId)
            .collection('activities')
            .orderBy('timestamp', 'asc');

          const oldSnapshot = await oldActivitiesRef.get();
          const activities = oldSnapshot.docs.map((doc) => {
            const data = doc.data() || {};
            return {
              id: doc.id,
              timestamp: data.timestamp?.toDate
                ? data.timestamp.toDate()
                : null,
              screenshots: data.screenshots || [],
              screenshotThumbs: Array.isArray(data.screenshotThumbs)
                ? data.screenshotThumbs
                : [],
              appUsage: data.appUsage || {},
              idleTime: data.idleTime || 0,
              totalTimeTracked: data.totalTimeTracked || '',
            };
          });
          console.log(
            `[getSessionActivities] Found ${activities.length} activities from old path`
          );
          return { success: true, data: { activities } };
        } catch (error) {
          console.error('[getSessionActivities] Old path query failed:', error);
          return { success: false, error };
        }
      }

      // If we reach here, we couldn't determine the path or both failed
      return {
        success: false,
        error:
          'Unable to fetch activities: missing path parameters or session not found',
      };
    } catch (error) {
      console.error('Error fetching session activities', error);
      return { success: false, error };
    }
  },

  /**
   * Fetch time tracking data using fetchTimeTrackingDataV2 API
   * Returns aggregated time data for a date range
   */
  async fetchTimeTrackingDataV2({
    teamId,
    companyId,
    userId,
    startDate,
    endDate,
    timezone,
  }) {
    if (
      !teamId ||
      !companyId ||
      !userId ||
      !startDate ||
      !endDate ||
      !timezone
    ) {
      return {
        success: false,
        error:
          'teamId, companyId, userId, startDate, endDate, and timezone are required',
      };
    }

    try {
      const requestPayload = {
        teamId,
        companyId,
        userId,
        startDate,
        endDate,
        timezone,
      };

      console.log('🔵 [fetchTimeTrackingDataV2] Request Payload:', {
        ...requestPayload,
        dateRange: `${startDate} to ${endDate}`,
        dateRangeDays:
          startDate && endDate
            ? Math.ceil(
                (new Date(endDate) - new Date(startDate)) /
                  (1000 * 60 * 60 * 24)
              ) + 1
            : 'N/A',
      });

      const response = await httpClient.post(
        '/fetchTimeTrackingDataV2',
        requestPayload
      );
      const result = response.data;

      console.log('🔵 [fetchTimeTrackingDataV2] Raw API Response:', {
        success: result.success,
        hasData: !!result.data,
        data: result.data,
        fullResponse: result,
      });

      if (result.success && result.data) {
        const apiData = result.data;
        console.log('🔵 [fetchTimeTrackingDataV2] API Data Breakdown:', {
          totalHours: apiData.totalHours,
          totalMinutes: apiData.totalMinutes,
          totalTimeTrackedSeconds: apiData.totalTimeTrackedSeconds,
          calculatedTotalSeconds:
            (apiData.totalHours || 0) * 3600 + (apiData.totalMinutes || 0) * 60,
          idleHours: apiData.idleHours,
          idleMinutes: apiData.idleMinutes,
          productiveHours: apiData.productiveHours,
          productiveMinutes: apiData.productiveMinutes,
          screenshotCount: apiData.screenshotCount,
          appUsageKeys: Object.keys(apiData.appUsage || {}).length,
        });

        return {
          success: true,
          data: result.data,
        };
      } else {
        console.error('🔴 [fetchTimeTrackingDataV2] API Error:', {
          success: result.success,
          message: result.message,
          error: result.error,
        });
        return {
          success: false,
          error: result.message || 'Failed to fetch time tracking data',
        };
      }
    } catch (error) {
      console.error('Error fetching time tracking data V2', error);
      const data = error.response?.data;
      return {
        success: false,
        error:
          data?.message ||
          error.message ||
          'An error occurred while fetching time tracking data',
      };
    }
  },

  /**
   * Fetch team time tracking data (GET) for a date range.
   * Returns aggregates and dailyBreakdown for the chart.
   */
  async fetchTeamTimeTrackingData({ teamId, startDate, endDate, timezone }) {
    if (!teamId || !startDate || !endDate || !timezone) {
      return {
        success: false,
        error: 'teamId, startDate, endDate, and timezone are required',
      };
    }
    try {
      const response = await httpClient.get('/fetchTeamTimeTrackingData', {
        params: { teamId, startDate, endDate, timezone },
      });
      const result = response.data;
      if (result.success && result.data) {
        return { success: true, data: result.data };
      }
      throw new Error(
        result.message || 'Failed to fetch department time tracking data'
      );
    } catch (error) {
      const data = error.response?.data;
      throw new Error(
        data?.message ||
          error.message ||
          'An error occurred while fetching department time tracking data'
      );
    }
  },
};
